import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { getConfigValue } from "../../config/runtimeConfig";

const defaultSuccessUrl =
  getConfigValue(
    "REACT_APP_PAYMENTS_SUCCESS_URL",
    process.env.REACT_APP_PAYMENTS_SUCCESS_URL,
    "https://teliviit.com/payments/success"
  );
const defaultCancelUrl =
  getConfigValue(
    "REACT_APP_PAYMENTS_CANCEL_URL",
    process.env.REACT_APP_PAYMENTS_CANCEL_URL,
    "https://teliviit.com/payments/cancel"
  );

const initialForm = {
  customerName: "",
  customerEmail: "",
  planId: "",
  successUrl: defaultSuccessUrl,
  cancelUrl: defaultCancelUrl,
};

const SubscriptionCheckoutPanel = ({
  onSubmit,
  plans,
  plansAvailable,
  plansLoading,
  onRefreshPlans,
  isSubmitting,
}) => {
  const [form, setForm] = useState(initialForm);

  const hasPlanOptions = plansAvailable && plans.length > 0;

  useEffect(() => {
    if (hasPlanOptions && !form.planId) {
      setForm((prev) => ({ ...prev, planId: String(plans[0].id) }));
    }
  }, [hasPlanOptions, plans, form.planId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const success = await onSubmit({
      ...form,
      planId: Number(form.planId),
    });
    if (success) {
      setForm(initialForm);
    }
  };

  return (
    <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
      <CardHeader>
        <Heading size="sm">Subscription Checkout</Heading>
      </CardHeader>
      <CardBody>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Generate a Stripe Checkout link for a subscription and share it with the customer.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel>Customer Name</FormLabel>
            <Input value={form.customerName} onChange={(e) => handleChange("customerName", e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Customer Email</FormLabel>
            <Input
              type="email"
              value={form.customerEmail}
              onChange={(e) => handleChange("customerEmail", e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Plan</FormLabel>
            {hasPlanOptions ? (
              <Select
                value={form.planId}
                onChange={(e) => handleChange("planId", e.target.value)}
                isDisabled={plansLoading}
              >
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({plan.currency} {plan.amount})
                  </option>
                ))}
              </Select>
            ) : (
              <NumberInput
                min={1}
                value={form.planId}
                onChange={(valueString) => handleChange("planId", valueString)}
              >
                <NumberInputField placeholder="Plan ID" />
              </NumberInput>
            )}
            {!hasPlanOptions ? (
              <HStack mt={2} justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  {plansAvailable
                    ? "No plans available yet. Enter a plan ID manually."
                    : "Plan list unavailable. Enter a plan ID manually."}
                </Text>
                <Button size="xs" variant="link" onClick={onRefreshPlans} isDisabled={plansLoading}>
                  Try fetching plans
                </Button>
              </HStack>
            ) : null}
          </FormControl>
          <FormControl>
            <FormLabel>Success URL</FormLabel>
            <Input value={form.successUrl} onChange={(e) => handleChange("successUrl", e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Cancel URL</FormLabel>
            <Input value={form.cancelUrl} onChange={(e) => handleChange("cancelUrl", e.target.value)} />
          </FormControl>
        </SimpleGrid>
        <Button
          mt={4}
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={!form.customerEmail || !form.planId}
        >
          Generate Checkout Link
        </Button>
      </CardBody>
    </Card>
  );
};

export default SubscriptionCheckoutPanel;
